import { useSites } from "../reducers/siteSlice";
import { useTranslation } from "react-i18next";
import useCredentials from "../common/hooks/credentialsHook";
import Select from "../common/components/Select";
import { useMemo } from "react";
import { dynamicSort } from "../common/utils/tableUtilities";
import { useLevels } from "../reducers/levelSlice";
import { useTypes } from "../reducers/typeSlice";
import Label from "../common/components/Label";
import { propagateFields } from "./customCtrlUtils";
import useClientConfig from "../common/hooks/clientConfigHook";
import { distinct } from "../common/utils/arrayUtilities";

export const customCtrlDef = {
    control: ({ def, onChange, site, level, type, context: { args } = {}, ...props }) => 
        <SiteDropdown site={site ?? args?.site ?? def.site} level={level} type={type ?? args?.type ?? def.typeParam} perm={def.perm} wildcard={def.wildcard} filter={def.filter} strict={def.strict} mode={def.mode} credentialsRoot={def.credentialsRoot} onChange={value => propagateFields(value, def.field, onChange)} {...props}></SiteDropdown>
}

const SiteDropdown = ({ id, site, level, type, perm, placeholder, wildcard, hideLabel = false, disabled = false, required = false, readonly = false, onChange, credentialsRoot = 'access', strict = true, autoHide = false , filter}) => {
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const { t } = useTranslation();
    const sites = useSites();
    const levels = useLevels();
    const types = useTypes();

    const userSites =  useMemo(() => {
        const access = credentials.access.accessTriples(clientConfig, 'full');
        return distinct(access?.map(a => a.site));
    }, [clientConfig, credentials]);

    const hideControl = useMemo(() => autoHide && perm === 'write' && userSites.length === 1 && site && userSites[0] === site, [autoHide, perm, site, userSites]);
  
    const displaySites = useMemo(() => {
        let displaySites = sites.filter(s => (!filter || filter(s)) && (s.name === site || perm === 'any' || credentials.access.has(['administrator','dispatcher']) || credentials[credentialsRoot][perm === 'write' ? 'hasFull' : 'has']({ site: s.name })));
        if (site && displaySites.every(s => s.name !== site)) {
            const toAdd = sites.find(s => s.name === site);
            if (toAdd) {
                displaySites.push(toAdd)
            }
        }
        if (!strict) {
            if (level) {
                displaySites = displaySites.filter(s => levels.some(l => l.name === level && (!l.site || l.site === s.name)));
            }
            if (type) {
                displaySites = displaySites.filter(s => types.some(t => t.type === type && (!t.level || !level || t.level === level) && (!t.site || t.site === s.name)));
            }
        }
        displaySites.sort(dynamicSort(['sequence','name']))
        return displaySites
    }, [sites, site, strict, filter, perm, credentials, credentialsRoot, level, type, levels, types]);
    
    if (hideControl) {
        return
    }
    if (readonly) {
        return <Label placeholder={placeholder} hideLabel={hideLabel} text={site ?? wildcard}></Label>
    }
    return (<Select id={id} placeholder={placeholder ?? t('site', { count: 1 })} wildcard={wildcard} hideLabel={hideLabel} value={site} onChange={site => onChange(site ? { site } : {site: null})} disabled={disabled} required={required} options={displaySites} labelField="name" valueField="name"></Select>)
}

export default SiteDropdown;